import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  LayoutList,
  Search,
  MapPin,
  Layers,
  Send,
  BarChart3,
  HelpCircle,
  Sparkles,
  CheckSquare,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Wordmark } from "@/shared/components/brand/Spark";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/shared/components/ui/tooltip";
import { useAuth } from "@/shared/auth/useAuth";
import { useTaskSummary } from "@/features/tasks/use-tasks";

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  /** Shown as a tooltip on hover — what the screen does. */
  hint: string;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

// Information architecture from screens.md: the sidebar is global; campaign-
// scoped screens live under a campaign and use the pipeline tab bar instead.
const NAV: NavGroup[] = [
  {
    label: "OVERVIEW",
    items: [
      {
        to: "/dashboard",
        icon: LayoutDashboard,
        label: "Dashboard",
        hint: "Your campaigns at a glance — KPIs, the funnel, deliverability, trend and recent activity.",
      },
    ],
  },
  {
    label: "ACQUIRE",
    items: [
      {
        to: "/campaigns",
        icon: LayoutList,
        label: "Campaigns",
        hint: "Your outreach campaigns — the container for discovery, leads, sequences and analytics.",
      },
      {
        to: "/discovery",
        icon: Search,
        label: "Discovery",
        hint: "Find leads by AI prompt or Google Places; every run is screened and logged.",
      },
      {
        to: "/leads",
        icon: MapPin,
        label: "Leads",
        hint: "The lead directory across campaigns — candidates, approved, duplicates and suppressed.",
      },
    ],
  },
  {
    label: "ENGAGE",
    items: [
      {
        to: "/tasks",
        icon: CheckSquare,
        label: "Tasks",
        hint: "Manual steps assigned to you from sequences — call, review, follow up.",
      },
      {
        to: "/sequences",
        icon: Layers,
        label: "Sequences",
        hint: "Build and manage outreach sequences and their steps.",
      },
      {
        to: "/outbox",
        icon: Send,
        label: "Outbox",
        hint: "Delivery events from the provider — sent, opened, replied, unsubscribed — across sequences.",
      },
    ],
  },
  {
    label: "MEASURE",
    items: [
      {
        to: "/analytics",
        icon: BarChart3,
        label: "Analytics",
        hint: "Portfolio performance — the funnel from discovered to signed up, and per-step rates.",
      },
    ],
  },
  {
    label: "SUPPORT",
    items: [
      {
        to: "/help",
        icon: HelpCircle,
        label: "Help",
        hint: "How to use the app — the pipeline, a step-by-step guide, and answers to common questions.",
      },
    ],
  },
];

// Admin-only groups (TENANT_ADMIN / SUPERADMIN). Appended when the caller is an admin.
const ADMIN_NAV: NavGroup[] = [
  {
    label: "SETTINGS",
    items: [
      {
        to: "/ai-profile",
        icon: Sparkles,
        label: "AI & Brand",
        hint: "Teach the AI your business, voice and brand kit, and manage reusable skills.",
      },
    ],
  },
];

// ADR-0027: a REP only works their task inbox — a minimal, task-only nav.
const REP_NAV: NavGroup[] = [
  {
    label: "WORK",
    items: [
      { to: "/tasks", icon: CheckSquare, label: "Tasks", hint: "Manual steps assigned to you — call, review, follow up." },
      { to: "/help", icon: HelpCircle, label: "Help", hint: "How to use the app." },
    ],
  },
];

export function Sidebar() {
  // Active state is computed here (not via NavLink's function className): the
  // tooltip trigger wraps the link with Radix Slot, which stringifies a function
  // className and breaks styling — so we pass a plain string className instead.
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = !!user && (user.superadmin || user.role === "TENANT_ADMIN");
  const isRep = !!user && user.role === "REP" && !user.superadmin;
  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
  const groups = isRep ? REP_NAV : isAdmin ? [...NAV, ...ADMIN_NAV] : NAV;

  // Pending-task badge on the Tasks item (red when any are overdue), like an inbox counter.
  const summary = useTaskSummary();
  const pending = summary.data?.mine.pending ?? 0;
  const overdue = summary.data?.mine.overdue ?? 0;

  return (
    <aside
      data-testid="sidebar"
      className="hidden w-sidebar flex-shrink-0 flex-col border-r border-ge-border-subtle bg-ge-sidebar px-3 pb-4 pt-[22px] md:flex"
    >
      <div className="px-[10px]">
        <Wordmark />
      </div>

      <nav className="mt-6 flex-1" aria-label="Primary">
        {groups.map((group) => (
          <div key={group.label} className="mb-4">
            <p className="ge-eyebrow px-[10px] pb-[6px]">{group.label}</p>
            {group.items.map((item) => {
              const Icon = item.icon;
              const id = item.to.replace(/^\//, "");
              const active = isActive(item.to);
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.to}
                      data-testid={`nav-${id}`}
                      className={cn(
                        "flex items-center gap-[10px] border-l-2 px-[10px] py-2 text-[13px] transition-colors",
                        active
                          ? "border-ge-accent bg-ge-active font-semibold text-ge-primary"
                          : "border-transparent font-medium text-ge-secondary hover:text-ge-primary",
                      )}
                    >
                      <Icon className="h-[15px] w-[15px]" strokeWidth={1.8} />
                      <span className="font-sans">{item.label}</span>
                      {item.to === "/tasks" && pending > 0 ? (
                        <span
                          data-testid="tasks-badge"
                          className={cn(
                            "ml-auto rounded-full px-[7px] py-[1px] text-[11px] font-semibold text-white",
                            overdue > 0 ? "bg-red-600" : "bg-ge-accent",
                          )}
                        >
                          {pending}
                        </span>
                      ) : null}
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right">{item.hint}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-ge-border-subtle px-[10px] pt-4">
        <p className="ge-eyebrow">SENDER HEALTH</p>
        <div className="mt-2 flex items-center gap-2">
          <span className="h-[7px] w-[7px] rounded-ge-control rounded-full bg-ge-accent" />
          <span className="text-[12px] text-ge-secondary">3 domains warm</span>
        </div>
      </div>
    </aside>
  );
}
